import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, getDocs, writeBatch, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getCurrentUserId } from '../../utils/auth';
import { MoreHorizontal } from 'lucide-react';
import './index.css';

const NotificationsPage = () => {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUserId, setCurrentUserId] = useState(null);

    useEffect(() => {
        const id = getCurrentUserId();
        if (!id) {
            navigate('/login');
            return;
        }
        setCurrentUserId(id);
    }, [navigate]);

    useEffect(() => {
        if (!currentUserId) return;

        const notifRef = collection(db, 'notifications', currentUserId, 'user_notifications');
        const q = query(notifRef, orderBy('timestamp', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const notifs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setNotifications(notifs);
            setLoading(false);

            // Auto mark all as read
            if (notifs.some(n => !n.isRead)) {
                markAllAsRead(notifs);
            }
        });

        return () => unsubscribe();
    }, [currentUserId]);

    const markAllAsRead = async (currentNotifs) => {
        if (!currentUserId) return;
        try {
            const notifRef = collection(db, 'notifications', currentUserId, 'user_notifications');
            const unreadQuery = query(notifRef, where('isRead', '==', false));
            const snapshot = await getDocs(unreadQuery);

            if (!snapshot.empty) {
                const batch = writeBatch(db);
                snapshot.docs.forEach((docSnap) => {
                    batch.update(docSnap.ref, { isRead: true });
                });
                await batch.commit();
            }
        } catch (error) {
            console.error("Error marking notifications as read:", error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (notification.senderId) {
            navigate('/user-profile', {
                state: {
                    sub_cat_id: notification.subcatId || '',
                    user_id: notification.senderId
                }
            });
        }
    };

    const parseTimestamp = (timestamp) => {
        if (!timestamp) return null;
        // Check if it's a Firestore Timestamp (has toDate method or seconds property)
        if (typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
        }
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000);
        }
        // Fallback for normal Date strings/numbers
        const parsed = new Date(timestamp);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatTimeAgo = (timestamp) => {
        const date = parseTimestamp(timestamp);
        if (!date) return '';
        
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) return `${diffInSeconds} s`;
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} m`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} h`;
        return `${Math.floor(diffInSeconds / 86400)} d`;
    };

    const formatDate = (timestamp) => {
        const date = parseTimestamp(timestamp);
        if (!date) return '';
        
        return date.toLocaleDateString('en-GB', {
            day: '2-digit', month: 'long', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        }).replace(' at ', ' , ');
    };

    // Grouping by "Today", "Yesterday", or specific dates could be added here, 
    // but for now we render a flat list matching mobile or a single section.
    
    if (loading) {
        return (
            <div className="notifications-page">
                <div className="notifications-header">
                    <h1 className="notifications-title">Notifications</h1>
                </div>
                <div className="notifications-container">
                    <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading notifications...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="notifications-page">
            <div className="notifications-header">
                <h1 className="notifications-title">Notifications</h1>
                <button className="mark-all-read" onClick={() => markAllAsRead(notifications)}>
                    Mark all as Read
                </button>
            </div>

            <div className="notifications-container">
                {notifications.length === 0 ? (
                    <div className="no-notifications-empty">
                        <p>No notifications found.</p>
                    </div>
                ) : (
                    <div className="notification-section">
                        {notifications.map((notification) => (
                            <div key={notification.id} className="notification-item" onClick={() => handleNotificationClick(notification)} style={{cursor: 'pointer'}}>
                                <div className="notification-left">
                                    <span className={`unread-dot ${!notification.isRead ? 'visible' : ''}`}></span>
                                    <div className="notification-avatar">
                                        <img 
                                            src={notification.senderImage ? `https://www.whysocial.in/clap-kartel/public/${notification.senderImage}` : 'https://placehold.co/100?text=NA'} 
                                            alt={notification.title || 'Notification'} 
                                            onError={(e) => { e.target.src = 'https://placehold.co/100?text=NA'; }}
                                        />
                                    </div>
                                    <div className="notification-content">
                                        <div className="notification-text">
                                            <span className="notification-name">{notification.title || 'Notification'}</span>
                                            <span className="notification-message">{notification.message}</span>
                                        </div>
                                        <p className="notification-date">{formatDate(notification.timestamp)}</p>
                                    </div>
                                </div>
                                <div className="notification-right">
                                    <span className="notification-time">{formatTimeAgo(notification.timestamp)}</span>
                                    {/* <button className="notification-more" onClick={(e) => e.stopPropagation()}>
                                        <MoreHorizontal size={20} />
                                    </button> */}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;